import React from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useActiveStore } from './StoreContext'
import { pickDefaultStore, readLastUsedStoreId, storeCodeSegment } from '../lib/storeRouting'
import { storePagePath } from '../lib/routes'
import type { StorePage } from '../lib/routes'
import styles from './RequireStore.module.scss'

const stateClassName = `row-flx flx-a-c flx-j-c p-v-40 p-h-16 txt-a-c ${styles['require-store']}`
const textClassName = `txt-light-kale ${styles['require-store__text']}`
const actionClassName = `m-t-16 txt-bold-500 txt-olive txt-decoration-none ${styles['require-store__action']}`

type RequireStoreProps = {
  /** Which store-scoped page this route belongs to (drives the redirect target). */
  page: StorePage
  /**
   * The page to render once a valid store is resolved. Omitted on the bare
   * redirect routes (`/`, `/propuesta`, `/pedidos`), which only bounce to a store.
   */
  children?: React.ReactNode
}

/**
 * Gate for the store-scoped routes. The store is part of the URL, so a route can
 * arrive with no code (`/propuesta`), an unknown/foreign code, or a valid one:
 *
 *   - stores still loading → a quiet loading state (never redirect yet).
 *   - user has no stores → a clear empty state (nothing to select).
 *   - no code at all → redirect to the default store (last-used, else first) on
 *     the same page — no error surfaced, the URL was simply incomplete.
 *   - a code that resolves to nothing → say the store is unavailable, with a
 *     link out. A bookmark to a deactivated store (or one this user lost access
 *     to) is a real situation and deserves an answer; the redirect that used to
 *     stand here rendered nothing at all whenever anything cancelled the
 *     navigation, e.g. the shell's unsaved-changes blocker.
 *   - valid code → render the page.
 *
 * Used both as the bare redirect element (no children) and as the wrapper around
 * each page, so all the "which store?" logic lives in one place.
 */
export const RequireStore = ({ page, children }: RequireStoreProps): React.ReactElement | null => {
  const { activeStore, stores, isLoading } = useActiveStore()
  // The router's own answer to "did the URL carry a code?" — the bare redirect
  // routes (`/`, `/propuesta`, `/pedidos`) have no :storeCode segment.
  const { storeCode } = useParams<{ storeCode: string }>()

  if (isLoading) {
    return (
      <div className={stateClassName} role="status" aria-live="polite">
        <span className={textClassName}>Cargando tiendas…</span>
      </div>
    )
  }

  if (stores.length === 0) {
    return (
      <div className={stateClassName}>
        <p className={textClassName}>
          No tienes ninguna tienda asignada. Contacta con tu responsable de Wetaca.
        </p>
      </div>
    )
  }

  if (activeStore === undefined) {
    const target = pickDefaultStore(stores, readLastUsedStoreId())
    // stores.length > 0 here, so pickDefaultStore always returns a store.
    if (target === undefined) return null
    const targetPath = storePagePath(page, storeCodeSegment(target))

    if (storeCode === undefined) {
      return <Navigate to={targetPath} replace />
    }

    return (
      <div className={stateClassName}>
        <div>
          <p className={textClassName}>
            Esta tienda ya no está disponible. Puede que se haya desactivado o que hayas dejado de
            tener acceso a ella.
          </p>
          <Link to={targetPath} replace className={actionClassName}>
            Ir a {target.name}
          </Link>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
